/**
 * Redes oficiales de AcademIA: la única lista en el código.
 *
 * El dato no vive en la base ni en el CMS. `companyInformation` (admin → Configuración,
 * `apps/api/src/settings/schemas/settings.schema.ts`) sólo tiene razón social, dirección,
 * CUIT, mail y matrícula; no hay ningún campo de redes, así que lo que se ve en el footer y
 * lo que declara el `sameAs` del JSON-LD de la home sale de acá. Antes el footer tenía su
 * propio array inline y los handles quedaron viejos sin que nadie lo notara: `@cursosacademia`
 * en X y en TikTok son cuentas de otras personas.
 *
 * Verificado el 2026-10-04:
 * - Instagram y Facebook: Graph API. Página `1302792779591323` ("Academia - Inteligencia
 *   Artificial", sitio cursosacademia.ar) e IG `17841435882756397` (`cursos.academia`,
 *   "AcademIA | Inteligencia Artificial", sitio cursosacademia.ar). La página de Facebook
 *   no tiene nombre de usuario: su URL canónica es la numérica que devuelve el campo `link`.
 * - TikTok: `@cursosacademia_hq`, confirmado por Rodrigo; el perfil se presenta como
 *   "cursosacademia.ar".
 * - X: `@cursosacademia_hq`, confirmado por Rodrigo (PR #37 / #38).
 * - LinkedIn: página de empresa "Cursos AcademIA", enlaza a cursosacademia.ar.
 * - YouTube: `@cursosacademia_hq` ("Cursos AcademIA", canal `UCt-oMxVZilhy1-ljeUNU6qA`), creado por
 *   Rodrigo el 2026-10-07.
 *
 * Los logos son los oficiales en una sola forma (viewBox 0 0 24 24, `fill: currentColor`),
 * inline para no sumar dependencias. Una red con `href: null` se muestra con «Próximamente», sin
 * enlace; al ponerle la URL, el footer y el `sameAs` se actualizan solos.
 */

export type SocialNetworkId =
  | "whatsapp"
  | "instagram"
  | "facebook"
  | "tiktok"
  | "linkedin"
  | "x"
  | "youtube";

export type SocialNetwork = {
  id: SocialNetworkId;
  /** Nombre de la red, tal como se lee. */
  label: string;
  /** Texto accesible del enlace. */
  ariaLabel: string;
  /** URL pública. `null` = la cuenta todavía no existe: se muestra el logo sin enlace. */
  href: string | null;
  /** `profile` entra en el `sameAs` de la organización; `contact` es un canal, no un perfil. */
  kind: "profile" | "contact";
  /** Trazo del logo, viewBox 0 0 24 24. */
  path: string;
};

export const SOCIAL_NETWORKS: readonly SocialNetwork[] = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    ariaLabel: "Escribinos por WhatsApp",
    // Chat vacío al número público: el mensaje lo escribe la persona.
    href: "https://wa.me/5491124061361",
    kind: "contact",
    path: "M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23a8.2 8.2 0 0 1 8.24 8.24c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.16 0-.43.06-.65.31-.22.25-.85.83-.85 2.03s.87 2.35.99 2.51c.12.16 1.71 2.61 4.15 3.66.58.25 1.03.4 1.39.51.58.19 1.11.16 1.53.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z",
  },
  {
    id: "instagram",
    label: "Instagram",
    ariaLabel: "AcademIA en Instagram",
    href: "https://www.instagram.com/cursos.academia/",
    kind: "profile",
    path: "M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.64-.07-4.85s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 2.7.27.27 2.69.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98 1.28.06 1.69.07 4.95.07s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95C23.73 2.7 21.31.27 16.95.07 15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zm0 10.15A4 4 0 1 1 12 8a4 4 0 0 1 0 8zm6.41-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z",
  },
  {
    id: "facebook",
    label: "Facebook",
    ariaLabel: "AcademIA en Facebook",
    // La página no tiene nombre de usuario: ésta es la URL canónica que devuelve Graph API.
    href: "https://www.facebook.com/1302792779591323",
    kind: "profile",
    path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  },
  {
    id: "tiktok",
    label: "TikTok",
    ariaLabel: "AcademIA en TikTok",
    href: "https://www.tiktok.com/@cursosacademia_hq",
    kind: "profile",
    path: "M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    ariaLabel: "AcademIA en LinkedIn",
    href: "https://www.linkedin.com/company/cursosacademia",
    kind: "profile",
    path: "M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z",
  },
  {
    id: "x",
    label: "X",
    ariaLabel: "AcademIA en X",
    href: "https://x.com/cursosacademia_hq",
    kind: "profile",
    path: "M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41z",
  },
  {
    id: "youtube",
    label: "YouTube",
    ariaLabel: "AcademIA en YouTube",
    href: "https://www.youtube.com/@cursosacademia_hq",
    kind: "profile",
    path: "M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.55 15.57V8.43L15.82 12l-6.27 3.57z",
  },
];

/** Perfiles que existen, para el `sameAs` del JSON-LD `Organization` de la home. */
export const SOCIAL_PROFILE_URLS: readonly string[] = SOCIAL_NETWORKS.flatMap((net) =>
  net.kind === "profile" && net.href ? [net.href] : [],
);
